import { Component, effect, inject, ViewChild, viewChildren, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { VoucherDetailService } from '../../../../pages/person/voucher-detail-page/voucher-detail-page.service';
import { VoucherDetailSituationFtStore } from './voucher-detail-situation-ft.store';
import { SituationFTSketchMarkingPoint } from '../../../../pages/person/voucher-detail-page/voucher-detail-page.types';
import { VoucherDetailSituationFtStore2 } from './voucher-detail-situation-ft.store2';
import { enableFormControls } from '../../../../../../utils/form.utils';
import { InputWysiwygComponent } from '../../../../../shared/components/inputs/input-wysiwyg/input-wysiwyg.component';
import { TranslatePipe } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { FormatAddressPipe } from '../../../../../shared/pipes/format-address.pipe';
import { DateInputComponent } from '../../../../../../components/kit/input/date-input/date-input.component';
import { FolderGlobalStore } from '../../../../../../store/medicalService/folder.global-store';

const POINT_SIZE = 10;
const MAX_DIS = POINT_SIZE * POINT_SIZE;

@Component({
    selector: 'app-voucher-detail-situation-ft',
    templateUrl: 'voucher-detail-situation-ft.component.html',
    styleUrls: ['voucher-detail-situation-ft.component.scss'],
    providers: [VoucherDetailSituationFtStore, VoucherDetailSituationFtStore2],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslatePipe,
        AsyncPipe,
        FormatAddressPipe,
        DateInputComponent,
        InputWysiwygComponent,
    ],
})
export class VoucherDetailSituationFtComponent {
    protected readonly store = inject(VoucherDetailSituationFtStore);
    private readonly detailService = inject(VoucherDetailService);


    public readonly store2 = inject(VoucherDetailSituationFtStore2);
    protected readonly folderStore = inject(FolderGlobalStore);

    public readonly wysiwygs = viewChildren<InputWysiwygComponent>(InputWysiwygComponent);

    @ViewChild('sketchCanvas') canvas!: any;
    @ViewChild('sketch') sketch!: any;

    form!: FormGroup;

    dependencyLevelOptions = [
        { name: 'voucher.situationORP.dependencyLevel.optionNone', value: 'none' },
        { name: 'voucher.situationORP.dependencyLevel.optionInProcess', value: 'inProcess' },
        { name: 'voucher.situationORP.dependencyLevel.option1stLevel', value: '1stLevel' },
        { name: 'voucher.situationORP.dependencyLevel.option2ndLevel', value: '2ndLevel' },
        { name: 'voucher.situationORP.dependencyLevel.option3rdLevel', value: '3rdLevel' },
        { name: 'voucher.situationORP.dependencyLevel.option4thLevel', value: '4thLevel' },
    ];

    mobilityOptions = [
        { name: 'voucher.situationORP.mobility.optionFull', value: 'full' },
        { name: 'voucher.situationORP.mobility.optionPartial', value: 'partial' },
        { name: 'voucher.situationORP.mobility.optionCompletelyImmobile', value: 'completelyImmobile' },
    ];

    selfServiceAbilityOptions = [
        { name: 'voucher.situationORP.selfServiceAbility.optionFull', value: 'full' },
        { name: 'voucher.situationORP.selfServiceAbility.optionPartial', value: 'partial' },
        { name: 'voucher.situationORP.selfServiceAbility.optionNone', value: 'none' },
    ];

    private points: SituationFTSketchMarkingPoint[] = [];
    private dragPoint: SituationFTSketchMarkingPoint | null = null;
    private dragStart = { x: 0, y: 0 };
    private dragOffset = { x: 0, y: 0 };
    private wh = { w: 0, h: 0 };
    private ctx!: CanvasRenderingContext2D;

    constructor() {
        this.form = this.detailService.situationFTForm;

        effect(() => {
            enableFormControls(this.form, this.folderStore.canEdit());
        });
    }

    public onLoad(): void {
        this.wh = { w: this.sketch.nativeElement.width, h: this.sketch.nativeElement.height };
        this.canvas.nativeElement.setAttribute('width', this.wh.w);
        this.canvas.nativeElement.setAttribute('height', this.wh.h);

        this.ctx = this.canvas.nativeElement.getContext('2d');
        this.ctx.fillStyle = 'red';

        this.calculatePointsCoordinatesFromVoucher();
        this.drawPoints();
    }

    protected click(event: any): void {
        event.stopPropagation();
        const x = event.pageX - event.target.offsetLeft;
        const y = event.pageY - event.target.offsetTop;

        const p = this.getPoint(x, y);
        if (this.dragPoint) {
            this.dragPoint.x = x - this.dragOffset.x;
            this.dragPoint.y = y - this.dragOffset.y;
            if (this.dragStart.x == x && this.dragStart.y == y) {
                this.points = this.points.filter((it) => it != p);
            }
        } else if (!p) {
            this.points.push({ x, y });
        }

        this.dragPoint = null;
        this.drawPoints();
        this.detailService.patchSituationFTForm({
            sketchMarking: this.calculatePointsCoordinatesToStore(),
        });
    }

    protected dragged(event: any): void {
        event.stopPropagation();
        if (this.dragPoint == null) {
            const x = event.pageX - event.target.offsetLeft;
            const y = event.pageY - event.target.offsetTop;
            this.dragPoint = this.getPoint(x, y);
            if (this.dragPoint) {
                this.dragStart = { x, y };
                this.dragOffset = { x: x - this.dragPoint!.x, y: y - this.dragPoint!.y };
            }
        }
    }

    protected dragging(event: any): void {
        if (this.dragPoint) {
            const x = event.pageX - event.target.offsetLeft;
            const y = event.pageY - event.target.offsetTop;
            this.dragPoint.x = x - this.dragOffset.x;
            this.dragPoint.y = y - this.dragOffset.y;
            this.drawPoints();
            this.detailService.patchSituationFTForm({
                sketchMarking: this.calculatePointsCoordinatesToStore(),
            });
        }
    }

    private getPoint(x: number, y: number): SituationFTSketchMarkingPoint | null {
        return this.points.find((p) => {
            const xx = x - p.x;
            const yy = y - p.y;

            return (xx * xx + yy * yy) <= MAX_DIS;
        }) || null;
    }

    private drawPoints(): void {
        this.ctx.clearRect(0, 0, this.wh.w, this.wh.h);
        for (const point of this.points) {
            this.ctx.beginPath();
            this.ctx.arc(point.x, point.y, POINT_SIZE, 0, 2 * Math.PI);
            this.ctx.fill();
        }
    }

    private calculatePointsCoordinatesToStore() {
        const canvasWidth = this.canvas.nativeElement.getBoundingClientRect().width;
        const canvasHeight = this.canvas.nativeElement.getBoundingClientRect().height;

        return this.points.map((p: SituationFTSketchMarkingPoint) => {
            return {
                x: Math.round((p.x / canvasWidth + Number.EPSILON) * 100) / 100,
                y: Math.round((p.y / canvasHeight + Number.EPSILON) * 100) / 100,
            };
        });
    }

    private calculatePointsCoordinatesFromVoucher() {
        const canvasWidth = this.canvas.nativeElement.getBoundingClientRect().width;
        const canvasHeight = this.canvas.nativeElement.getBoundingClientRect().height;

        this.points = this.form?.value?.sketchMarking?.map((p: SituationFTSketchMarkingPoint) => {
            return {
                x: p.x * canvasWidth,
                y: p.y * canvasHeight,
            };
        }) || [];
    }
}
